import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FormFields,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';

function ServiceAdministratorProductLineEdit() {
  const { formatMessage } = useLocalization();
  const editFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/service-administrator-product-lines/${pageId}`;
  const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/service-administrator-product-lines/${pageId}`;

  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState(null);

  const preProcessRequestData = data => {
    const {
      status,
      createdByUserId,
      lastModifiedByUserId,
      id,
      industries,
      recordVersion,
      ...other
    } = data;
    return {
      status: status === 'true',
      industries:
        industries?.map(i => {
          return { id: i.value };
        }) || [],
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const { industries, ...other } = data;
    return {
      ...other,
      industries:
        industries?.map(i => {
          return { label: i.industryName, value: i.id };
        }) || [],
    };
  };

  const submitEditForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEADMINISTRATORPRODUCTLINE_UPDATE_SUCCESS',
          defaultMessage:
            'Service Administrator Product Line was saved successfully',
        }),
      );
      setInitialValues(preProcessFetchData(response?.data));
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEADMINISTRATORPRODUCTLINE_UPDATE_FAILURE',
          defaultMessage:
            'Error: Could not save the service administrator product line',
        }),
      });
    }
  };

  const fetchInitialData = async () => {
    setFormErrors(null);
    const response = await get(getPageDataApiUrl);
    if (response?.ok) {
      setInitialValues({
        ...initialValues,
        ...preProcessFetchData(response?.data),
      });
      setLoading(false);
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEADMINISTRATORPRODUCTLINE_DATA_FETCH_FAILURE',
          defaultMessage:
            'Error: Could not fetch the service administrator product line data',
        }),
      });
    }
  };

  useEffect(() => {
    fetchInitialData(pageId);
  }, [pageId]);

  const formProps = {
    customComponents: FormFields,
    schema: editSchema,
    onSubmit: submitEditForm,
    ref: editFormRef,
    initialValues,
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEADMINISTRATORPRODUCTLINES_BREADCRUMB',
        defaultMessage: 'Service Administrator Product Lines',
      }),
      icon: IconLibrary.ServiceAdministratorProductLines,
      onClick: () => {
        navigate('/serviceadministratorproductlines');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_SERVICEADMINISTRATORPRODUCTLINE_EDIT_BREADCRUMB',
        defaultMessage: 'Edit Service Administrator Product Line',
      }),
      icon: IconLibrary.ServiceAdministratorProductLine,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_SERVICEADMINISTRATORPRODUCTLINES_STACKABLE_BUTTON',
        defaultMessage: 'Create Service Administrator Product Line',
      }),
      Icon: IconLibrary.ServiceAdministratorProductLine,
      variant: 'outlined',
      onClick: () => navigate('/serviceadministratorproductlines/create'),
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_DETAIL_PAGES',
    },
    {
      label: formatMessage({
        id: 'EDIT_SERVICEADMINISTRATORPRODUCTLINES_STACKABLE_BUTTON',
        defaultMessage: 'List Service Administrator Product Lines',
      }),
      Icon: IconLibrary.ServiceAdministratorProductLines,
      variant: 'outlined',
      onClick: () => navigate('/serviceadministratorproductlines'),
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_DETAIL_PAGES',
    },
  ];

  return (
    <Layout>
      {loading && (
        <LoadingEvent mt={4}>
          <FormattedMessage id="PROCESSING" defaultMessage="Processing..." />
        </LoadingEvent>
      )}

      {!loading && (
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
      )}

      {!loading && (
        <PageSection
          label={formatMessage({
            id: 'SERVICEADMINISTRATORPRODUCTLINE_EDIT_HEADING',
            defaultMessage: 'Edit Service Administrator Product Line',
          })}>
          <Box>
            <CRUDComponent
              errors={formErrors}
              type={CRUD_COMPONENT_TYPE.FORM}
              formProps={formProps}>
              <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
                <PermissionButton
                  permissionName="CAN_CREATE_EDIT_MAINTENANCE_DETAIL_PAGES"
                  variant="contained"
                  startIcon={<IconLibrary.Save />}
                  color="primary"
                  onClick={() => editFormRef.current?.submit()}>
                  {formatMessage({
                    id: 'SERVICEADMINISTRATORPRODUCTLINE_UPDATE_SAVE_BUTTON',
                    defaultMessage: 'Save Service Administrator Product Line',
                  })}
                </PermissionButton>
                <PermissionButton
                  permissionName="CAN_CREATE_EDIT_MAINTENANCE_DETAIL_PAGES"
                  variant="contained"
                  color="primary"
                  startIcon={<IconLibrary.Reset />}
                  onClick={() => editFormRef.current?.reset()}>
                  {formatMessage({
                    id: 'SERVICEADMINISTRATORPRODUCTLINE_UPDATE_RESET_BUTTON',
                    defaultMessage: 'Reset',
                  })}
                </PermissionButton>
              </Box>
            </CRUDComponent>
          </Box>
        </PageSection>
      )}
    </Layout>
  );
}

export default observer(ServiceAdministratorProductLineEdit);
